import memoize from "@-/com.npmjs.memoize";
import { useWindowDimensions, View } from "@-/com.npmjs.react-native";
import Svg, {
  Circle,
  Defs,
  Ellipse,
  G,
  LinearGradient,
  Mask,
  Path,
  RadialGradient,
  Rect,
  Stop,
} from "@-/com.npmjs.react-native-svg";
import { memo, useCallback, useId, useMemo, useRef, useState } from "react";
import { SPACE_BACKDROP_COLORS, SPACE_CHROME_COLOR } from "#space-backdrop-palette";
import { useIsSpaceBackgroundEnabled } from "#use-is-space-background-enabled";
import {
  createSpaceBackdropStarfield,
  type SpaceBackdropGlint,
  type SpaceBackdropStar,
} from "#space-backdrop-starfield";

/**
 * Fades the sky out across its surface instead of painting it solid, so an edge band can dissolve
 * content into the sky itself rather than into a flat colour that hides the stars.
 */
export interface SpaceBackdropFade {
  /** Where the ramp ends (its last stop), as fractions of the surface. */
  end: { x: number; y: number };
  /** `[fractionAlongAxis, alpha]` stops, from `start` to `end`. */
  ramp: readonly (readonly [number, number])[];
  /** Where the ramp starts (its first stop), as fractions of the surface. */
  start: { x: number; y: number };
}

/** The surface this instance covers, in window (and so sky) coordinates. */
interface SpaceBackdropFrame {
  height: number;
  width: number;
  x: number;
  y: number;
}

/** Stars are drawn as one path per opacity step instead of one SVG node each. */
const STAR_OPACITY_STEPS = 4;

/** Where each nebula sits, as fractions of the window (art-directed, not random). */
const NEBULA_PLACEMENTS = [
  { centerX: 0.86, centerY: 0.1, radiusX: 0.85, radiusY: 0.32 },
  { centerX: 0.06, centerY: 0.55, radiusX: 0.8, radiusY: 0.46 },
  { centerX: 0.72, centerY: 0.98, radiusX: 0.8, radiusY: 0.3 },
] as const;

function circlePath(x: number, y: number, radius: number): string {
  return `M${x - radius},${y}a${radius},${radius} 0 1,0 ${2 * radius},0a${radius},${radius} 0 1,0 ${-2 * radius},0`;
}

/** A four-pointed glint whose arms curve in to a narrow waist. */
function glintPath({ size, x, y }: SpaceBackdropGlint): string {
  const waist = size * 0.13;
  return [
    `M${x},${y - size}`,
    `Q${x + waist},${y - waist} ${x + size},${y}`,
    `Q${x + waist},${y + waist} ${x},${y + size}`,
    `Q${x - waist},${y + waist} ${x - size},${y}`,
    `Q${x - waist},${y - waist} ${x},${y - size}`,
    "Z",
  ].join("");
}

function starPathsByOpacity(stars: readonly SpaceBackdropStar[]): { d: string; opacity: number }[] {
  const steps = Array.from({ length: STAR_OPACITY_STEPS }, () => [] as string[]);
  for (const star of stars) {
    const step = Math.min(STAR_OPACITY_STEPS - 1, Math.floor(star.opacity * STAR_OPACITY_STEPS));
    steps[step]?.push(circlePath(star.x, star.y, star.radius));
  }
  return steps.map((paths, step) => ({
    d: paths.join(""),
    opacity: (step + 0.5) / STAR_OPACITY_STEPS,
  }));
}

/**
 * The sky for a window size, as SVG path data. Shared by every surface showing it, so the same
 * window size is laid out once however many surfaces draw it.
 */
const getSpaceBackdropSkyPaths = memoize(
  (width: number, height: number) => {
    const starfield = createSpaceBackdropStarfield({ height, width });
    const { edges, stars } = starfield.constellation;
    return {
      band: starfield.band,
      constellationLines: edges
        .map(([from, to]) => {
          const start = stars[from];
          const end = stars[to];
          return start === undefined || end === undefined
            ? ""
            : `M${start.x},${start.y}L${end.x},${end.y}`;
        })
        .join(""),
      constellationStars: stars,
      glints: starfield.glints,
      glintStars: starfield.glints.map((glint) => glintPath(glint)).join(""),
      stars: starPathsByOpacity(starfield.stars),
    };
  },
  { cacheKey: ([width, height]) => `${width}x${height}` },
);

const SpaceBackdropSky = memo(function SpaceBackdropSky({
  fade,
  frame,
  height,
  width,
}: {
  fade: SpaceBackdropFade | undefined;
  frame: SpaceBackdropFrame;
  height: number;
  width: number;
}) {
  const colors = SPACE_BACKDROP_COLORS;
  const sky = getSpaceBackdropSkyPaths(width, height);
  const { band } = sky;
  // Ids are document-global on web, where every instance's mask differs.
  const fadeId = useId();

  return (
    <Svg height={height} width={width}>
      <Defs>
        {colors.nebulae.map((nebula, index) => (
          <RadialGradient cx="50%" cy="50%" id={`space-nebula-${index}`} key={index} r="50%">
            <Stop offset="0" stopColor={nebula.color} stopOpacity={nebula.opacity} />
            <Stop offset="0.55" stopColor={nebula.color} stopOpacity={nebula.opacity * 0.35} />
            <Stop offset="1" stopColor={nebula.color} stopOpacity={0} />
          </RadialGradient>
        ))}
        <RadialGradient cx="50%" cy="50%" id="space-band" r="50%">
          <Stop offset="0" stopColor={colors.band} stopOpacity={colors.bandOpacity} />
          <Stop offset="0.5" stopColor={colors.band} stopOpacity={colors.bandOpacity * 0.45} />
          <Stop offset="1" stopColor={colors.band} stopOpacity={0} />
        </RadialGradient>
        <RadialGradient cx="50%" cy="50%" id="space-glint-halo" r="50%">
          <Stop offset="0" stopColor={colors.glintHalo} stopOpacity={colors.glintHaloOpacity} />
          <Stop offset="1" stopColor={colors.glintHalo} stopOpacity={0} />
        </RadialGradient>
        {fade === undefined ? null : (
          <>
            <LinearGradient
              gradientUnits="userSpaceOnUse"
              id={`${fadeId}-ramp`}
              x1={frame.x + fade.start.x * frame.width}
              x2={frame.x + fade.end.x * frame.width}
              y1={frame.y + fade.start.y * frame.height}
              y2={frame.y + fade.end.y * frame.height}
            >
              {fade.ramp.map(([offset, alpha]) => (
                <Stop key={offset} offset={offset} stopColor="#ffffff" stopOpacity={alpha} />
              ))}
            </LinearGradient>
            <Mask
              height={frame.height}
              id={`${fadeId}-mask`}
              maskUnits="userSpaceOnUse"
              width={frame.width}
              x={frame.x}
              y={frame.y}
            >
              <Rect
                fill={`url(#${fadeId}-ramp)`}
                height={frame.height}
                width={frame.width}
                x={frame.x}
                y={frame.y}
              />
            </Mask>
          </>
        )}
      </Defs>
      <G mask={fade === undefined ? undefined : `url(#${fadeId}-mask)`}>
        <Rect fill={SPACE_CHROME_COLOR} height={height} width={width} />
        {NEBULA_PLACEMENTS.map((placement, index) => (
          <Ellipse
            cx={placement.centerX * width}
            cy={placement.centerY * height}
            fill={`url(#space-nebula-${index})`}
            key={index}
            rx={placement.radiusX * width}
            ry={placement.radiusY * height}
          />
        ))}
        <G origin={`${band.centerX}, ${band.centerY}`} rotation={band.angleDegrees}>
          <Ellipse
            cx={band.centerX}
            cy={band.centerY}
            fill="url(#space-band)"
            rx={band.radiusX}
            ry={band.radiusY}
          />
        </G>
        {sky.stars.map((starPath, index) =>
          starPath.d === "" ? null : (
            <Path d={starPath.d} fill={colors.star} fillOpacity={starPath.opacity} key={index} />
          ),
        )}
        <Path
          d={sky.constellationLines}
          fill="none"
          stroke={colors.constellation}
          strokeLinecap="round"
          strokeOpacity={colors.constellationOpacity}
          strokeWidth={0.8}
        />
        {sky.constellationStars.map((star, index) => (
          <Circle
            cx={star.x}
            cy={star.y}
            fill="url(#space-glint-halo)"
            key={`constellation-halo-${index}`}
            r={6}
          />
        ))}
        <Path
          d={sky.constellationStars.map((star) => circlePath(star.x, star.y, 1.7)).join("")}
          fill={colors.star}
          fillOpacity={0.95}
        />
        {sky.glints.map((glint, index) => (
          <Circle
            cx={glint.x}
            cy={glint.y}
            fill="url(#space-glint-halo)"
            key={`glint-halo-${index}`}
            opacity={glint.opacity}
            r={glint.size * 2.4}
          />
        ))}
        <Path d={sky.glintStars} fill={colors.glint} fillOpacity={0.95} />
      </G>
    </Svg>
  );
});

/** Draws the window's sky, offset so this surface shows exactly the part of it behind it. */
function SpaceBackdropWindowSky({ fade }: { fade: SpaceBackdropFade | undefined }) {
  const { height, width } = useWindowDimensions();
  const ref = useRef<View>(null);
  const [frame, setFrame] = useState<SpaceBackdropFrame | null>(null);
  const onLayout = useCallback(() => {
    ref.current?.measureInWindow((windowX, windowY, frameWidth, frameHeight) => {
      const next = {
        height: Math.round(frameHeight),
        width: Math.round(frameWidth),
        x: Math.round(windowX),
        y: Math.round(windowY),
      };
      setFrame((previous) =>
        previous?.x === next.x &&
        previous.y === next.y &&
        previous.width === next.width &&
        previous.height === next.height
          ? previous
          : next,
      );
    });
  }, []);
  const offset = useMemo(() => (frame === null ? null : { x: frame.x, y: frame.y }), [frame]);

  return (
    <View
      className="absolute inset-0 overflow-hidden"
      onLayout={onLayout}
      pointerEvents="none"
      ref={ref}
      testID="space-backdrop"
    >
      {offset === null || width === 0 || height === 0 ? null : (
        <View
          className="absolute"
          pointerEvents="none"
          style={{ height, left: -offset.x, top: -offset.y, width }}
        >
          <SpaceBackdropSky
            fade={fade}
            frame={frame ?? { height: 0, width: 0, x: 0, y: 0 }}
            height={Math.round(height)}
            width={Math.round(width)}
          />
        </View>
      )}
    </View>
  );
}

/**
 * The experimental outer-space backdrop: a deep navy sky with nebulae, a galaxy band, stars and
 * 北斗, opaque over the surface it fills. Place it as the first child of an opaque `bg-muted`
 * chrome surface; it ignores touches.
 *
 * Every instance draws the same window-sized sky, offset by its own position in the window (like
 * CSS `background-attachment: fixed`), so the status band, toolbars, deck and tab bar read as one
 * continuous sky however they are nested. With `fade`, the sky's opacity follows a ramp across
 * the surface instead (see `ScrollEdgeFade`). Renders nothing unless Settings → Admin → Admin
 * settings → "Space background" is on.
 */
export function SpaceBackdrop({ fade }: { fade?: SpaceBackdropFade }) {
  return useIsSpaceBackgroundEnabled() ? <SpaceBackdropWindowSky fade={fade} /> : null;
}
